/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import clsx from 'clsx'
import {
    BuildingOfficeIcon,
    CheckCircleIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    MapPinIcon,
    UserIcon,
} from '@heroicons/react/24/solid'
import {
    BriefcaseIcon,
    CalendarIcon,
    DocumentTextIcon,
} from '@heroicons/react/24/outline'
import { Company } from 'types/entities/Company'
import UiCompanyTypePill from 'components/Ui/UiCompanyTypePill'
import { CompanyBranchType } from 'types/enums/CompanyBranchType'
import MergeCompaniesContactsList from './MergeCompaniesContactsList'
import MergeCompaniesAddressesList from './MergeCompaniesAddressesList'
import { getCompanyDisplayName } from 'utils/companyUtils'

interface MergeCompaniesCardsListProps {
    companies: Company[]
    fullDetailsMap: Record<number, Company>
    selectedForMergeIds: Set<number>
    selectedMainId: number | null
    expandedRows: Record<number, boolean>
    duplicateEmails: Set<string>
    onToggleMergeSelection: (id: number) => void
    onSelectMainId: (id: number) => void
    onToggleRow: (id: number) => void
    translate: (value: string) => string
}

const MergeCompaniesCardsList = ({
    companies,
    fullDetailsMap,
    selectedForMergeIds,
    selectedMainId,
    expandedRows,
    duplicateEmails,
    onToggleMergeSelection,
    onSelectMainId,
    onToggleRow,
    translate,
}: MergeCompaniesCardsListProps) => {
    if (!companies.length) {
        return null
    }

    return (
        <div className="MergeCompaniesCardsList">
            {companies.map((company) => {
                const fullDetail =
                    fullDetailsMap[company.id as number] || company

                const isSelectedForMerge = selectedForMergeIds.has(
                    company.id as number
                )
                const isMain = selectedMainId === company.id
                const isExpanded = expandedRows[company.id as number]

                return (
                    <div
                        key={company.id}
                        className={clsx(
                            'relative overflow-hidden rounded-2xl border-2 transition-all duration-300 group',
                            isMain
                                ? 'border-primary-500 bg-primary-50/30 dark:bg-primary-900/10 shadow-lg shadow-primary-500/10'
                                : isSelectedForMerge
                                  ? 'border-primary-200 dark:border-primary-800 bg-white dark:bg-gray-900'
                                  : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 opacity-70 grayscale-[0.5] hover:opacity-100 hover:grayscale-0'
                        )}
                    >
                        <div className="p-5 flex flex-col md:flex-row gap-6">
                            <div className="flex md:flex-col gap-3 justify-center items-center border-r dark:border-gray-800 pr-6">
                                <div className="flex flex-col items-center gap-1">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase">
                                        {translate('Merge')}
                                    </span>
                                    <button
                                        onClick={() =>
                                            onToggleMergeSelection(
                                                company.id as number
                                            )
                                        }
                                        className={clsx(
                                            'w-8 h-8 rounded-lg border-2 flex items-center justify-center transition-all',
                                            isSelectedForMerge
                                                ? 'bg-primary-500 border-primary-500 text-white'
                                                : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-transparent'
                                        )}
                                    >
                                        <CheckCircleIcon className="w-5 h-5" />
                                    </button>
                                </div>

                                <div
                                    className={clsx(
                                        'flex flex-col items-center gap-1 transition-opacity',
                                        !isSelectedForMerge &&
                                            'opacity-30 pointer-events-none'
                                    )}
                                >
                                    <span className="text-[10px] font-bold text-gray-400 uppercase">
                                        {translate('Main')}
                                    </span>
                                    <button
                                        onClick={() =>
                                            onSelectMainId(
                                                company.id as number
                                            )
                                        }
                                        className={clsx(
                                            'w-10 h-10 rounded-xl border-2 flex items-center justify-center transition-all',
                                            isMain
                                                ? 'bg-primary-500 border-primary-500 text-white shadow-inner'
                                                : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-300'
                                        )}
                                    >
                                        <CheckCircleIcon className="w-7 h-7" />
                                    </button>
                                </div>
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-2">
                                    <div
                                        className={clsx(
                                            'w-10 h-10 rounded-xl flex items-center justify-center transition-colors',
                                            isMain
                                                ? 'bg-primary-500 text-white'
                                                : 'bg-gray-100 dark:bg-gray-800 text-gray-400'
                                        )}
                                    >
                                        <BuildingOfficeIcon className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-bold text-lg text-gray-900 dark:text-white leading-tight">
                                                {getCompanyDisplayName(company)}
                                            </h3>
                                            <UiCompanyTypePill
                                                type={
                                                    company.type as CompanyBranchType
                                                }
                                            />
                                        </div>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-[11px] font-bold text-gray-400 tracking-widest uppercase">
                                                ID: {company.id}
                                            </span>
                                            {(company.cin ||
                                                company.vat_number) && (
                                                <>
                                                    <span className="w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-700" />
                                                    <div className="flex gap-2">
                                                        {company.cin && (
                                                            <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md font-medium border border-blue-100 dark:border-blue-800/50">
                                                                CIN: {company.cin}
                                                            </span>
                                                        )}
                                                        {company.vat_number && (
                                                            <span className="text-[10px] px-1.5 py-0.5 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-md font-medium border border-indigo-100 dark:border-indigo-800/50">
                                                                VAT:{' '}
                                                                {company.vat_number}
                                                            </span>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {(company.email || company.phone) && (
                                    <div className="flex flex-wrap gap-4 mt-4 py-3 border-y border-gray-100 dark:border-gray-800/50">
                                        {company.email && (
                                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                <span className="text-gray-400">
                                                    @
                                                </span>
                                                <span className="truncate">
                                                    {company.email}
                                                </span>
                                            </div>
                                        )}
                                        {company.phone && (
                                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                <span className="text-gray-400">
                                                    PH
                                                </span>
                                                <span>{company.phone}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-3 gap-3 md:w-80 h-fit">
                                <div className="bg-orange-50 dark:bg-orange-950/20 p-3 rounded-2xl border border-orange-100 dark:border-orange-900/30 flex flex-col items-center">
                                    <BriefcaseIcon className="w-5 h-5 text-orange-600 dark:text-orange-400 mb-1" />
                                    <span className="text-[10px] font-bold text-orange-800/60 dark:text-orange-400/60 uppercase tracking-tighter">
                                        {translate('Leads')}
                                    </span>
                                    <span className="text-xl font-bold text-orange-900 dark:text-orange-100">
                                        {fullDetail.leads?.length ?? 0}
                                    </span>
                                </div>
                                <div className="bg-blue-50 dark:bg-blue-950/20 p-3 rounded-2xl border border-blue-100 dark:border-blue-900/30 flex flex-col items-center">
                                    <DocumentTextIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mb-1" />
                                    <span className="text-[10px] font-bold text-blue-800/60 dark:text-blue-400/60 uppercase tracking-tighter">
                                        {translate('Offers')}
                                    </span>
                                    <span className="text-xl font-bold text-blue-900 dark:text-blue-100">
                                        {fullDetail.offers?.length ?? 0}
                                    </span>
                                </div>
                                <div className="bg-emerald-50 dark:bg-emerald-950/20 p-3 rounded-2xl border border-emerald-100 dark:border-emerald-900/30 flex flex-col items-center">
                                    <CalendarIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mb-1" />
                                    <span className="text-[10px] font-bold text-emerald-800/60 dark:text-emerald-400/60 uppercase tracking-tighter">
                                        {translate('Res.')}
                                    </span>
                                    <span className="text-xl font-bold text-emerald-900 dark:text-emerald-100">
                                        {fullDetail.reservations?.length ?? 0}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div
                            className={clsx(
                                'border-t border-gray-100 dark:border-gray-800 transition-all duration-300',
                                isExpanded
                                    ? 'bg-gray-50/50 dark:bg-gray-800/20'
                                    : ''
                            )}
                        >
                            <div
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onToggleRow(company.id as number)
                                }}
                                className="px-5 py-3 flex items-center justify-between text-xs font-semibold text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors pointer-events-auto"
                            >
                                <div className="flex gap-4">
                                    <span>
                                        {translate('Persons')}:{' '}
                                        {fullDetail.contacts?.length ?? 0}
                                    </span>
                                    <span>
                                        {translate('Addresses')}:{' '}
                                        {fullDetail.addresses?.length ?? 0}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1">
                                    {isExpanded
                                        ? translate('Collapse')
                                        : translate('Show Details')}
                                    {isExpanded ? (
                                        <ChevronUpIcon className="w-4 h-4" />
                                    ) : (
                                        <ChevronDownIcon className="w-4 h-4" />
                                    )}
                                </div>
                            </div>

                            {isExpanded && (
                                <div className="px-5 pb-5 grid md:grid-cols-2 gap-6 animate-in slide-in-from-top-2 duration-200">
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2 mb-2 text-xs font-bold text-gray-400 uppercase tracking-widest">
                                            <UserIcon className="w-3.5 h-3.5" />
                                            {translate('Contact Persons')}
                                        </div>
                                        <div className="grid gap-2">
                                            <MergeCompaniesContactsList
                                                contacts={fullDetail.contacts}
                                                emptyLabel={translate(
                                                    'No contact persons assigned'
                                                )}
                                                duplicateEmails={duplicateEmails}
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2 mb-2 text-xs font-bold text-gray-400 uppercase tracking-widest">
                                            <MapPinIcon className="w-3.5 h-3.5" />
                                            {translate('Addresses')}
                                        </div>
                                        <div className="grid gap-2">
                                            <MergeCompaniesAddressesList
                                                addresses={fullDetail.addresses}
                                                emptyLabel={translate(
                                                    'No addresses assigned'
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

export default MergeCompaniesCardsList
